/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { reset, submit } from 'redux-form';
import { useHistory, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { AutoparkModalContent } from './components/autopark-modal-content';
import {
	createAutoparkAction,
	updateAutoparkAction,
	getAutoparkInfoAction,
	deleteAutoparkAction,
	getAutoparkCompaniesListAction,
	resetStateAction,
} from './actions';
import {
	getAutoparkInfoSelector,
	getAutoparkNameSelector,
	getIsAuditTabShownSelector,
	getIsAutoparkAddFormOpenSelector,
	getIsAutoparkEditFormOpenSelector,
	getIsAutoparkInfoLoadingSelector,
	getIsModalShownSelector,
} from './selectors';
import { getIsAvailableAutoparksButtonsSelector } from '../profile/selectors';
import { setWindowInfoAction } from '../alert-modal/actions';
import { Audit } from '../../components/audit';
import { AUTOPARK_FORM_NAME } from './constants';
import { ModalHeader } from './components/modal-header';
import closeModal from '../../utils/closeModal';
import { removeModalQuery } from '../../utils/removeModalQuery';

const AutoparkModalWrapper = ({
	isAvailable,
	onGetAutoparkInfo,
	isAutoparkInfoLoading,
	autoparkInfo,
	autoparkName,
	onResetState,
	onFormSubmit,
	onClearForm,
	onCreateAutopark,
	onUpdateAutopark,
	onDeleteAutopark,
	onSetWindowInfo,
	isModalShown,
	isAuditTabShown,
	isAutoparkAddFormOpen,
	isAutoparkEditFormOpen,
	onGetAutoparkCompaniesList,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'autoparkModalSaga', saga });
	useInjectReducer({ key: 'autoparkModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const modal = query.get('modal');

	const id = query.get('id');

	const mode = query.get('mode');

	const history = useHistory();

	useEffect(() => {
		return () => onResetState();
	}, []);

	useEffect(() => {
		if (id) {
			onGetAutoparkInfo(id);
		}
	}, [id]);

	useEffect(() => {
		if (isAutoparkEditFormOpen || isAutoparkAddFormOpen) {
			onGetAutoparkCompaniesList();
		}
	}, [isAutoparkAddFormOpen, isAutoparkEditFormOpen]);

	const handleModalClose = () => {
		closeModal(query);

		onClearForm();
		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const handleNavItemClick = modal => {
		if (modal === 'audit') {
			query.set('tab', 'audit');
		} else {
			query.set('modal', modal);
			query.delete('tab');
		}

		if (mode) {
			query.delete('mode');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleAddTransportButtonClick = () => {
		query.set('modal', 'transport');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handleAutoparkFormOpenButtonClick = mode => {
		if (id) {
			query.set('id', id);
		} else {
			query.delete('id');
		}

		if (mode === 'edit') {
			query.set('mode', 'edit');
		}

		if (mode === 'cancel') {
			onClearForm();
			query.delete('mode');
		}

		if (mode === 'cancel' && !id) {
			onResetState();
			onClearForm();
			query.delete('modal');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleAutoparkFormSubmit = async () => {
		await onFormSubmit(AUTOPARK_FORM_NAME);

		if (id) {
			onUpdateAutopark({
				id,
				searchQuery: removeModalQuery(query.toString()),
				redirect: () => handleAutoparkFormOpenButtonClick('cancel'),
			});
			return;
		}

		onCreateAutopark({
			redirect: handleModalClose,
			searchQuery: removeModalQuery(query.toString()),
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleAutoparkFormSubmit();
		}
	};

	const handleAutoparkDelete = () => {
		onSetWindowInfo({
			type: 'delete',
			title: 'Вы уверены?',
			text: 'Вы не сможете это отменить',
			button: {
				type: 'success',
				text: 'Да, удалить',
				onClick: () =>
					onDeleteAutopark({
						id,
						redirect: handleModalClose,
						searchQuery: removeModalQuery(query.toString()),
					}),
			},
		});
	};

	return (
		<ModalWindow
			isLoading={isAutoparkInfoLoading}
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			navItems={
				isAutoparkEditFormOpen || isAutoparkAddFormOpen
					? []
					: [
							{
								id: 'info',
								text: 'Автопарк',
								onClick: () => handleNavItemClick('autopark'),
								isActive: modal === 'autopark' && !isAuditTabShown,
							},
							{
								id: 'audit',
								text: 'Изменения',
								onClick: () => handleNavItemClick('audit'),
								isActive: isAuditTabShown,
							},
					  ]
			}
			modalHeader={
				<ModalHeader
					onAddButtonClick={
						!(isAutoparkEditFormOpen || isAutoparkAddFormOpen) && !isAuditTabShown && isAvailable.update
							? handleAddTransportButtonClick
							: null
					}
					onModalClose={handleModalClose}
					title={
						isAutoparkEditFormOpen || isAutoparkAddFormOpen
							? `${isAutoparkEditFormOpen ? 'Изменить автопарк' : 'Создать автопарк'}`
							: autoparkName
					}
				/>
			}
			modalContent={
				!isAuditTabShown ? (
					<AutoparkModalContent
						autoparkInfo={autoparkInfo}
						isAutoparkInfoLoading={isAutoparkInfoLoading}
						isAutoparkFormOpen={isAutoparkEditFormOpen || isAutoparkAddFormOpen}
					/>
				) : (
					<Audit entityName="autoparks" entityId={id} />
				)
			}
			style={{ width: '895px' }}
			footerButtons={
				<ModalFooterButtons
					leftButtons={
						!(isAutoparkEditFormOpen || isAutoparkAddFormOpen) && isAvailable.update && !isAuditTabShown
							? [
									{
										onClick: handleAutoparkDelete,
										text: 'Удалить',
										color: 'warning',
										disabled: isAutoparkInfoLoading,
									},
							  ]
							: []
					}
					rightButtons={
						isAutoparkEditFormOpen || isAutoparkAddFormOpen
							? [
									{
										onClick: handleAutoparkFormSubmit,
										text: 'Сохранить',
										color: 'primary',
										loading: isAutoparkInfoLoading,
										disabled: isAutoparkInfoLoading,
									},
									{
										onClick: () => handleAutoparkFormOpenButtonClick('cancel'),
										text: 'Отмена',
										color: 'secondary',
										disabled: isAutoparkInfoLoading,
									},
							  ]
							: [
									...(isAvailable.update && !isAuditTabShown
										? [
												{
													onClick: () => handleAutoparkFormOpenButtonClick('edit'),
													text: 'Изменить',
													color: 'primary',
													disabled: isAutoparkInfoLoading,
												},
										  ]
										: []),
									{
										onClick: handleModalClose,
										text: 'Закрыть',
										color: 'secondary',
									},
							  ]
					}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	autoparkInfo: getAutoparkInfoSelector(),
	isAutoparkInfoLoading: getIsAutoparkInfoLoadingSelector(),
	autoparkName: getAutoparkNameSelector(),
	isModalShown: getIsModalShownSelector(),
	isAvailable: getIsAvailableAutoparksButtonsSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isAutoparkAddFormOpen: getIsAutoparkAddFormOpenSelector(),
	isAutoparkEditFormOpen: getIsAutoparkEditFormOpenSelector(),
});

const mapDispatchToProps = {
	onGetAutoparkInfo: getAutoparkInfoAction,
	onDeleteAutopark: deleteAutoparkAction,
	onResetState: resetStateAction,
	onFormSubmit: submit,
	onClearForm: () => reset(AUTOPARK_FORM_NAME),
	onCreateAutopark: createAutoparkAction,
	onUpdateAutopark: updateAutoparkAction,
	onSetWindowInfo: setWindowInfoAction,
	onGetAutoparkCompaniesList: getAutoparkCompaniesListAction,
};

const AutoparkModal = connect(mapStateToProps, mapDispatchToProps)(AutoparkModalWrapper);

AutoparkModalWrapper.propTypes = {
	onGetAutoparkInfo: PropTypes.func.isRequired,
	onDeleteAutopark: PropTypes.func.isRequired,
	autoparkInfo: PropTypes.object,
	isAutoparkInfoLoading: PropTypes.bool.isRequired,
	autoparkName: PropTypes.string.isRequired,
	onResetState: PropTypes.func.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	onClearForm: PropTypes.func.isRequired,
	onCreateAutopark: PropTypes.func.isRequired,
	onUpdateAutopark: PropTypes.func.isRequired,
	isModalShown: PropTypes.bool.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	onSetWindowInfo: PropTypes.func.isRequired,
	isAuditTabShown: PropTypes.bool.isRequired,
	isAutoparkAddFormOpen: PropTypes.bool.isRequired,
	isAutoparkEditFormOpen: PropTypes.bool.isRequired,
	onGetAutoparkCompaniesList: PropTypes.func.isRequired,
};

export default AutoparkModal;
